import Card from "@/components/Card";
import FormHeader from "@/components/FormHeader";
import Modal from "@/components/Modal";
import SkeletonTable from "@/components/SkeletonTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/axios";
import { DataKepemimpinan } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  FileText,
  GraduationCap,
  Hash,
  Loader2,
  Phone,
  School,
  User,
} from "lucide-react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaArrowLeftLong, FaFaceGrinBeam } from "react-icons/fa6";
import { LuCheck } from "react-icons/lu";
import { MdClose, MdHistory } from "react-icons/md";

type DataPendaftaran = {
  id_data_pendaftaran: string;
  nomor_pendaftaran: string;
  nisn: string;
  nama_lengkap: string;
  handphone: string;
  npsn_sekolah: string;
  nama_sekolah: string;
  pilihan1: string;
  pilihan2: string;
  path_foto: string;
};

type CombinedDataPendaftaran = DataPendaftaran &
  DataKepemimpinan & {
    nama_organisasi: string;
    lingkup: string;
    nomor_sk: string;
    tahun_sk: string;
    [key: string]: any;
  };

type ResponseKepemimpinan = {
  dataPendaftaran: CombinedDataPendaftaran;
  verifikatorKe: number;
  jenisVerifikator: string;
};

type StateVerifikasi = {
  id_data_kepemimpinan: string;
  verifikator_ke: string | number;
  jabatan_ver1?: string;
  validasi_ver1?: number;
  ket_ver1?: string;
  jabatan_ver2?: string;
  validasi_ver2?: number;
  ket_ver2?: string;
  jabatan_ver3?: string;
  validasi_ver3?: number;
  ket_ver3?: string;
};

const getDetailJabatanKepemimpinan = async (id_data_pendaftaran: string) => {
  const response = await api.get(
    `/verifikator/peserta-kepemimpinan-smbjm/detail?id_data_pendaftaran=${id_data_pendaftaran}`,
  );
  return response.data?.data;
};

const patchDataVerifikasi = async (data: StateVerifikasi) => {
  const response = await api.patch(
    `/verifikator/peserta-kepemimpinan-smbjm/validate?verifikatorKe=${data?.verifikator_ke}`,
    data,
  );
  return response.data;
};

const postResetVerifikasi = async (data: {
  id_data_kepemimpinan: string;
  verifikatorKe: number;
}) => {
  const response = await api.patch(
    `/verifikator/peserta-kepemimpinan-smbjm/reset-validate`,
    data,
  );
  return response.data;
};

// Helper component for uniform Badge rendering
const RenderStatusBadge = ({ value }: { value: number }) => {
  if (value === null)
    return (
      <Badge
        variant="secondary"
        className="dark:bg-slate-800 dark:text-slate-300 border-transparent shadow-none whitespace-nowrap"
      >
        Belum diverifikasi
      </Badge>
    );
  if (value === 1)
    return (
      <Badge
        variant="success"
        className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-transparent hover:bg-emerald-200 dark:hover:bg-emerald-900/60 shadow-none whitespace-nowrap"
      >
        Valid
      </Badge>
    );
  if (value === 0)
    return (
      <Badge variant="destructive" className="shadow-none whitespace-nowrap">
        Tidak Valid
      </Badge>
    );
  return null;
};

export default function FormVerifikasi({
  id_data_pendaftaran,
  onClose,
}: {
  id_data_pendaftaran: String | null;
  onClose: () => void;
}) {
  const { data, isPending } = useQuery<ResponseKepemimpinan>({
    queryKey: ["detailJabatanKepemimpinan", id_data_pendaftaran],
    queryFn: () => getDetailJabatanKepemimpinan(String(id_data_pendaftaran)),
    enabled: !!id_data_pendaftaran,
  });

  const selectedKepemimpinan = data?.dataPendaftaran;
  console.log({ selectedKepemimpinan });

  const [showModalVerifikasi, setShowModalVerifikasi] = useState(false);
  const [showConfirmationReset, setShowConfirmationReset] = useState(false);

  const [form, setForm] = useState({
    jabatan_ver1: "",
    validasi_ver1: 0,
    ket_ver1: "",
    jabatan_ver2: "",
    validasi_ver2: 0,
    ket_ver2: "",
    jabatan_ver3: "",
    validasi_ver3: 0,
    ket_ver3: "",
  });

  const viewerRef = useRef<HTMLElement>(null);

  const { data: jabatanKepemimpinanData } = useQuery<{
    status: string;
    data: { nama_jabatan: string; point: number }[];
  }>({
    queryKey: ["jabatanKepemimpinanList"],
    queryFn: async () => {
      const response = await api.get("/master/jabatan-kepemimpinan/index");
      return response.data;
    },
  });

  const verifikatorKe = data?.verifikatorKe ?? 0;
  const keyJabatan = `jabatan_ver${verifikatorKe}` as keyof typeof form;
  const keyKet = `ket_ver${verifikatorKe}` as keyof typeof form;
  const keyValidasi = `validasi_ver${verifikatorKe}` as keyof typeof form;
  // const keyNamaVerifikator = `nama_ver${verifikatorKe}` as keyof typeof form;

  const queryClient = useQueryClient();

  const { mutate: mutateReset, isPending: isPendingReset } = useMutation({
    mutationFn: postResetVerifikasi,
    onSuccess: (resData) => {
      queryClient.invalidateQueries({
        queryKey: ["detailJabatanKepemimpinan"],
      });
      queryClient.invalidateQueries({
        queryKey: ["listDataPesertaKepemimpinanSmbjm"],
      });
      toast.success(resData?.message || "Reset berhasil.");
      setShowConfirmationReset(false);
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(err?.response?.data?.message || "Gagal melakukan reset.");
    },
  });

  const handleReset = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedKepemimpinan?.id_data_kepemimpinan && verifikatorKe) {
      mutateReset({
        id_data_kepemimpinan: selectedKepemimpinan.id_data_kepemimpinan,
        verifikatorKe,
      });
    }
  };

  const { mutate, isPending: isPendingVerifikasi } = useMutation({
    mutationFn: patchDataVerifikasi,
    onSuccess: (resData) => {
      queryClient.invalidateQueries({
        queryKey: ["detailJabatanKepemimpinan"],
      });
      queryClient.invalidateQueries({
        queryKey: ["listDataPesertaKepemimpinanSmbjm"],
      });
      toast.success(resData?.message || "Success.");

      setForm((prev) => ({
        ...prev,
        id_data_kepemimpinan: selectedKepemimpinan?.id_data_kepemimpinan,
        jabatan_ver1: "",
        validasi_ver1: 0,
        ket_ver1: "",
        jabatan_ver2: "",
        validasi_ver2: 0,
        ket_ver2: "",
        jabatan_ver3: "",
        validasi_ver3: 0,
        ket_ver3: "",
      }));

      setShowModalVerifikasi(false);
      setShowConfirmationReset(false);
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleVerifikasi = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!verifikatorKe) return;

    const payload = {
      id_data_kepemimpinan: selectedKepemimpinan?.id_data_kepemimpinan || "",
      verifikator_ke: verifikatorKe,
      [`validasi_ver${verifikatorKe}`]:
        form[`validasi_ver${verifikatorKe}` as keyof typeof form],
      [`jabatan_ver${verifikatorKe}`]:
        form[`jabatan_ver${verifikatorKe}` as keyof typeof form],
      [`ket_ver${verifikatorKe}`]:
        form[`ket_ver${verifikatorKe}` as keyof typeof form],
    };
    mutate(payload);
  };

  const isVerifikatorPertama = verifikatorKe == 1;
  const isVerifikatorKedua = verifikatorKe == 2;
  const isVerifikatorKetiga = verifikatorKe == 3;

  const getVerifikatorLabel = () => {
    if (isVerifikatorPertama) return "Pertama";
    if (isVerifikatorKedua) return "Kedua";
    if (isVerifikatorKetiga) return "Ketiga";
    return "";
  };

  console.log(selectedKepemimpinan);

  if (isPending) return <SkeletonTable />;

  return (
    <div className="text-slate-900 dark:text-slate-100 transition-colors duration-200 h-full flex flex-col">
      {/* HEADER SECTION */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
        <h1 className="text-xl sm:text-2xl font-bold flex flex-wrap items-center gap-3">
          Verifikasi Berkas
          <Badge className="text-xs sm:text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500/20 dark:text-blue-300 dark:border dark:border-blue-500/30">
            Verifikator {getVerifikatorLabel()}
          </Badge>
        </h1>
        <Button
          variant="ghost"
          onClick={onClose}
          className="flex items-center w-full sm:w-auto justify-center gap-2 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-400 border sm:border-transparent"
        >
          Tutup Formulir <MdClose size={18} />
        </Button>
      </div>

      {/* MAIN CONTENT AREA */}
      {/* Container ini sekarang mengalir pada mobile, dan dibatasi h-[80vh] pada Desktop */}
      <div className="flex flex-col lg:flex-row gap-6 mt-4 lg:mt-6 lg:h-[80vh]">
        {/* LEFT PANE - STUDENT DATA */}
        <section className="w-full lg:w-[45%] flex flex-col gap-5 lg:overflow-y-auto lg:pr-2 custom-scrollbar lg:pb-10">
          {/* Organisasi & SK Info Card */}
          <Card className="border-slate-200 dark:border-slate-800 shadow-sm dark:bg-slate-950">
            <CardHeader className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800/80 py-4">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <FileText className="h-5 w-5 text-purple-500" />
                Detail Organisasi & SK (Isian Siswa)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="space-y-1">
                <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Nama Organisasi
                </p>
                <p className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">
                  {selectedKepemimpinan?.nama_organisasi || "-"}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Jabatan
                  </p>
                  <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200">
                    {selectedKepemimpinan?.jabatan || "-"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Lingkup
                  </p>
                  <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200 uppercase">
                    {selectedKepemimpinan?.lingkup?.replace(/_/g, " ") || "-"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Nomor SK
                  </p>
                  <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200 break-all">
                    {selectedKepemimpinan?.nomor_sk || "-"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Tahun SK
                  </p>
                  <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200">
                    {selectedKepemimpinan?.tahun_sk || "-"}
                  </p>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">
                  Acuan Verifikasi:
                </h3>
                <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed text-justify">
                  Peserta dinyatakan memenuhi ketentuan Jalur Kepemimpinan
                  apabila mengunggah SK Kepengurusan yang menunjukkan bahwa
                  peserta merupakan pengurus inti organisasi, yaitu sebagai
                  Ketua, Wakil Ketua, Sekretaris, atau Bendahara, baik pada
                  organisasi di dalam maupun di luar sekolah.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Data Pendaftaran Siswa */}
          <Card className="border-slate-200 dark:border-slate-800 shadow-sm dark:bg-slate-950">
            <CardHeader className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800/80 py-4">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <FileText className="h-5 w-5 text-blue-500" />
                Data Pendaftaran Siswa
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                {/* Photo */}
                <div className="flex-shrink-0 flex justify-center sm:justify-start">
                  <div className="w-28 sm:w-32 aspect-[3/4] h-fit overflow-hidden rounded-xl border-4 border-slate-100 dark:border-slate-800 shadow-sm relative bg-slate-100 dark:bg-slate-800">
                    {data?.dataPendaftaran?.path_foto ? (
                      <img
                        src={data?.dataPendaftaran?.path_foto}
                        alt="foto siswa"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-12 h-12 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="flex-1 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <User className="h-3.5 w-3.5" /> Nama Lengkap
                      </p>
                      <p className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">
                        {data?.dataPendaftaran?.nama_lengkap}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <Hash className="h-3.5 w-3.5" /> No. Pendaftaran
                      </p>
                      <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200">
                        {data?.dataPendaftaran?.nomor_pendaftaran}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <Hash className="h-3.5 w-3.5" /> NISN
                      </p>
                      <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200">
                        {data?.dataPendaftaran?.nisn}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                        <Phone className="h-3.5 w-3.5" /> No. Handphone
                      </p>
                      <p className="font-medium text-sm sm:text-base text-slate-800 dark:text-slate-200">
                        {data?.dataPendaftaran?.handphone}
                      </p>
                    </div>
                  </div>

                  <Separator className="dark:bg-slate-800" />

                  <div className="space-y-4">
                    <div className="space-y-1">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2 mb-2">
                        <School className="h-3.5 w-3.5" /> Asal Sekolah
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <p className="font-semibold text-sm sm:text-base text-slate-900 dark:text-white">
                          {data?.dataPendaftaran?.nama_sekolah}
                        </p>
                        <Badge
                          variant="outline"
                          className="w-fit dark:border-slate-700 dark:text-slate-300"
                        >
                          NPSN: {data?.dataPendaftaran?.npsn_sekolah}
                        </Badge>
                      </div>
                    </div>

                    <div className="space-y-2 pt-2">
                      <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2 mb-2">
                        <GraduationCap className="h-3.5 w-3.5" /> Program Studi
                        Pilihan
                      </p>
                      <div className="flex flex-col gap-2">
                        {data?.dataPendaftaran?.pilihan1 && (
                          <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 shadow-none border-transparent whitespace-nowrap">
                              Pilihan 1
                            </Badge>
                            <span className="font-medium text-xs sm:text-sm text-slate-800 dark:text-slate-200">
                              {data?.dataPendaftaran?.pilihan1}
                            </span>
                          </div>
                        )}
                        {data?.dataPendaftaran?.pilihan2 && (
                          <div className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                            <Badge
                              variant="secondary"
                              className="shadow-none dark:bg-slate-700 dark:text-slate-300 whitespace-nowrap"
                            >
                              Pilihan 2
                            </Badge>
                            <span className="font-medium text-xs sm:text-sm text-slate-800 dark:text-slate-200">
                              {data?.dataPendaftaran?.pilihan2}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 3: Riwayat Verifikasi Sebelumnya */}
          {isVerifikatorKetiga && (
            <Card className="border-slate-200 dark:border-slate-800 shadow-sm dark:bg-slate-950">
              <CardHeader className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800/80 py-4">
                <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <MdHistory className="h-5 w-5 text-orange-500" />
                  Riwayat Verifikasi Sebelumnya
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-4">
                <div className="space-y-4">
                  {/* Verifikator 1 */}
                  <div className="p-3 border rounded-lg dark:border-slate-800 bg-slate-50 dark:bg-slate-900/30">
                    <h4 className="font-semibold text-sm mb-3 text-slate-700 dark:text-slate-300">
                      Verifikator Pertama
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Nama
                        </p>
                        <p className="font-medium text-slate-800 dark:text-slate-200">
                          {selectedKepemimpinan?.nama_ver1 || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Jabatan
                        </p>
                        <p className="font-medium text-slate-800 dark:text-slate-200">
                          {selectedKepemimpinan?.jabatan_ver1 || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Nilai
                        </p>
                        <p className="font-medium text-slate-800 dark:text-slate-200">
                          {selectedKepemimpinan?.nilai_ver1 ?? "-"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Verifikator 2 */}
                  <div className="p-3 border rounded-lg dark:border-slate-800 bg-slate-50 dark:bg-slate-900/30">
                    <h4 className="font-semibold text-sm mb-3 text-slate-700 dark:text-slate-300">
                      Verifikator Kedua
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Nama
                        </p>
                        <p className="font-medium text-slate-800 dark:text-slate-200">
                          {selectedKepemimpinan?.nama_ver2 || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Jabatan
                        </p>
                        <p className="font-medium text-slate-800 dark:text-slate-200">
                          {selectedKepemimpinan?.jabatan_ver2 || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                          Nilai
                        </p>
                        <p className="font-medium text-slate-800 dark:text-slate-200">
                          {selectedKepemimpinan?.nilai_ver2 ?? "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </section>

        {/* RIGHT PANE - DOCUMENT VIEWER & ACTIONS */}
        <section
          ref={viewerRef}
          className="w-full lg:w-[55%] flex flex-col border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50 dark:bg-slate-900/30 overflow-hidden relative shadow-inner min-h-[600px] lg:min-h-0"
        >
          {selectedKepemimpinan ? (
            <>
              {/* Sticky Action Header */}
              <div className="z-10 sticky top-0 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 p-4 shadow-sm flex flex-col xl:flex-row items-start xl:items-center justify-between gap-4">
                <div className="space-y-2 w-full xl:w-auto">
                  <div className="flex items-center justify-between sm:justify-start gap-3 w-full">
                    <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                      Berkas Kepemimpinan
                    </h2>
                    <RenderStatusBadge
                      value={selectedKepemimpinan?.[keyValidasi] as number}
                    />
                  </div>
                  {selectedKepemimpinan?.[keyValidasi] !== null &&
                    selectedKepemimpinan?.[keyValidasi] !== undefined && (
                      <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                        {selectedKepemimpinan?.[keyValidasi] !== 0 && (
                          <>
                            Jabatan:
                            <Badge
                              variant="outline"
                              className="font-bold text-sm sm:text-base bg-white dark:bg-slate-900 dark:border-slate-700"
                            >
                              {selectedKepemimpinan?.[keyJabatan]}
                            </Badge>
                          </>
                        )}
                        <span className="mx-1 text-slate-300 dark:text-slate-700">
                          |
                        </span>
                        Point:
                        <Badge
                          variant="outline"
                          className="font-bold text-sm sm:text-base bg-white dark:bg-slate-900 dark:border-slate-700"
                        >
                          {selectedKepemimpinan?.[
                            `nilai_ver${verifikatorKe}`
                          ] ?? "-"}
                        </Badge>
                      </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
                  {selectedKepemimpinan?.[keyValidasi] !== null &&
                    selectedKepemimpinan?.[keyValidasi] !== undefined && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 dark:border-slate-700"
                        onClick={() => setShowConfirmationReset(true)}
                      >
                        <MdHistory className="w-4 h-4 text-slate-500" />
                        <span className="hidden sm:inline">Reset Hasil</span>
                        <span className="sm:hidden">Reset</span>
                      </Button>
                    )}
                  <Button
                    size="sm"
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white hover:text-white dark:bg-blue-600/90 dark:hover:bg-blue-600"
                    onClick={() => {
                      setShowModalVerifikasi(true);
                      if (
                        form[keyValidasi] === 0 &&
                        selectedKepemimpinan?.[keyValidasi] === 0
                      ) {
                        setForm((prev) => ({ ...prev, [keyValidasi]: 1 }));
                      }
                    }}
                  >
                    Verifikasi Berkas <LuCheck size={18} />
                  </Button>
                </div>
              </div>

              {/* Iframe Viewer */}
              <div className="flex-1 w-full bg-slate-200 dark:bg-slate-950 p-2 sm:p-4 min-h-[400px]">
                <div className="w-full h-full rounded-lg overflow-hidden shadow-lg border border-slate-300 dark:border-slate-800 bg-white">
                  <iframe
                    src={selectedKepemimpinan?.file_sk || ""}
                    className="w-full h-full min-h-[500px] lg:min-h-full"
                    title={`Kepemimpinan `}
                  />
                </div>
              </div>
            </>
          ) : (
            /* Empty State */
            <div className="flex-1 flex flex-col justify-center items-center text-slate-400 dark:text-slate-500 p-6 sm:p-8 text-center bg-white/50 dark:bg-slate-950/50">
              <FaFaceGrinBeam size={50} className="mb-4 sm:mb-6 opacity-50" />
              <h3 className="text-lg sm:text-2xl font-bold flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-slate-500 dark:text-slate-400">
                <FaArrowLeftLong className="animate-pulse hidden lg:block" />
                Pilih berkas dari tabel untuk melihat dokumen
              </h3>
              <p className="mt-2 text-sm sm:text-base max-w-sm">
                Klik tombol "Lihat" pada tabel di atas untuk menampilkan preview
                berkas kepemimpinan di sini.
              </p>
            </div>
          )}
        </section>
      </div>

      {/* MODALS */}
      <Modal
        show={showModalVerifikasi}
        onClose={() => setShowModalVerifikasi(false)}
      >
        <form
          onSubmit={handleVerifikasi}
          className="bg-white dark:bg-slate-900 rounded-lg p-1 text-slate-900 dark:text-white"
        >
          <FormHeader
            title="Form Verifikasi Jabatan"
            description="Lakukan validasi berkas dan sesuaikan jabatan apabila diperlukan."
            icon={<></>}
          />
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label className="dark:text-slate-300">
                Validitas <span className="text-red-500">*</span>
              </Label>
              <Select
                required
                value={String(form[keyValidasi] === 2 ? 0 : form[keyValidasi])}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    [keyValidasi]: Number(value),
                  }))
                }
              >
                <SelectTrigger className="w-full bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                  <SelectValue placeholder="Pilih Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Valid</SelectItem>
                  <SelectItem value="0">Tidak Valid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form[keyValidasi] === 1 && (
              <div className="space-y-2">
                <Label className="dark:text-slate-300">
                  Jabatan yang Tertera pada SK{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Select
                  required
                  value={String(form[keyJabatan] || "")}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      [keyJabatan]: value,
                    }))
                  }
                >
                  <SelectTrigger className="w-full bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
                    <SelectValue placeholder="Pilih Jabatan" />
                  </SelectTrigger>
                  <SelectContent>
                    {jabatanKepemimpinanData?.data?.map((jabatan, index) => (
                      <SelectItem key={index} value={jabatan.nama_jabatan}>
                        {jabatan.nama_jabatan}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="dark:text-slate-300">
                Keterangan / Alasan{" "}
                {(form[keyValidasi] === 0 || form[keyValidasi] === 2) && (
                  <span className="text-red-500">*</span>
                )}
              </Label>
              <Textarea
                required={form[keyValidasi] === 0 || form[keyValidasi] === 2}
                placeholder="Tambahkan catatan jika diperlukan..."
                rows={3}
                value={form[keyKet]}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, [keyKet]: e.target.value }))
                }
                className="dark:bg-slate-950 dark:border-slate-700 dark:text-white"
              />
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModalVerifikasi(false)}
              disabled={isPendingVerifikasi}
              className="w-full sm:w-auto dark:border-slate-700 dark:text-slate-300"
            >
              Batal
            </Button>
            <Button
              disabled={isPendingVerifikasi}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isPendingVerifikasi ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                  Processing...
                </>
              ) : (
                "Simpan Validasi"
              )}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showConfirmationReset}
        onClose={() => setShowConfirmationReset(false)}
      >
        <form
          onSubmit={handleReset}
          className="bg-white dark:bg-slate-900 rounded-lg p-1 text-slate-900 dark:text-white"
        >
          <FormHeader
            title="Konfirmasi Reset"
            description="Apakah anda yakin ingin mengembalikan status verifikasi berkas ini menjadi 'Belum diverifikasi'?"
            icon={<></>}
          />
          <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmationReset(false)}
              disabled={isPendingReset}
              className="w-full sm:w-auto dark:border-slate-700 dark:text-slate-300"
            >
              Batal
            </Button>
            <Button
              variant="secondary"
              disabled={isPendingReset}
              className="w-full sm:w-auto dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
            >
              {isPendingReset ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                  Processing...
                </>
              ) : (
                "Ya, Reset Hasil"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
